/**
 * crossSceneAudio — a MODULE-SCOPE persistent background-audio player that survives viewer
 * recreation, so an ambient music bed can play continuously across portal teleports.
 *
 * Why module scope: a portal teleport fully tears the viewer down and rebuilds it
 * (handlePortalNavigation → cleanupForPortalNavigation → new createViewer). Anything the viewer
 * owns — AudioContext, spatial audio emitters, narration music — stops on teleport. This player
 * lives outside any viewer instance, so the bed keeps playing without a hiccup across every jump.
 *
 * Behaviour:
 *  - ensure(url, opts): start the bed; if the SAME url is already active, keep playing untouched
 *    (the seamless-teleport case). A DIFFERENT url soft-crossfades, so a downstream scene can take
 *    over the music. A scene that declares no cross-scene audio simply doesn't call this, and the
 *    current bed persists — that's the "set it at the entry point and it plays the whole time" model.
 *  - unlock(): browsers block autoplay until a user gesture; the viewer calls this on the same
 *    first-interaction / unmute gesture it already uses for narration.
 *  - setMuted() / setVolume(): wired to the viewer's global mute + the configured volume.
 *  - clear(): full stop + release. Auto-fires on page unload (pagehide) so the bed never leaks
 *    after the tab closes; also exposed on the viewer instance API for hosts (the editor) to call.
 */

export interface CrossSceneAudioOptions {
    /** 0..1, default 1. */
    volume?: number;
    /** default true. */
    loop?: boolean;
    /** Crossfade when switching to a different track, default true. */
    fade?: boolean;
}

const FADE_MS = 600;

let el: HTMLAudioElement | null = null;
let activeUrl: string | null = null;
let targetVolume = 1;
let muted = false;
let unlocked = false;
let fadeTimer: ReturnType<typeof setInterval> | null = null;
let pagehideBound = false;
// The live self-unlock handler, or null when disarmed. Tracked (rather than using
// { once: true }) so all three listeners can be removed together the instant any one
// fires — otherwise the un-fired siblings linger and a later re-arm would stack them.
let gestureUnlockHandler: (() => void) | null = null;

const GESTURE_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;
const clampVol = (v: number): number => Math.max(0, Math.min(1, v));
const effectiveVolume = (): number => (muted ? 0 : clampVol(targetVolume));

function disarmGestureUnlock(): void {
    if (gestureUnlockHandler && typeof window !== 'undefined') {
        const h = gestureUnlockHandler;
        GESTURE_EVENTS.forEach(ev => window.removeEventListener(ev, h));
    }
    gestureUnlockHandler = null;
}

/**
 * Self-unlock on the first user gesture anywhere — browsers block autoplay until then, so the
 * bed starts as soon as the visitor interacts even if no other audio prompted them. Re-armable:
 * clear() disarms + resets `unlocked`, so a fresh bed on the SAME page (e.g. after a host teardown
 * or a bfcache pagehide) can unlock again instead of being permanently stuck silent.
 */
function armGestureUnlock(): void {
    if (gestureUnlockHandler || unlocked || typeof window === 'undefined') return;
    const handler = (): void => {
        disarmGestureUnlock();
        crossSceneAudio.unlock();
    };
    gestureUnlockHandler = handler;
    GESTURE_EVENTS.forEach(ev => window.addEventListener(ev, handler, { passive: true }));
}

function ensureEl(): HTMLAudioElement {
    if (!el) {
        el = new Audio();
        el.loop = true;
        el.preload = 'auto';
        el.crossOrigin = 'anonymous';
        if (!pagehideBound && typeof window !== 'undefined') {
            // Never leak the bed after the tab/experience closes. Registered once — the anonymous
            // handler is idempotent (clear() is safe to call repeatedly) and never needs removal.
            window.addEventListener('pagehide', () => crossSceneAudio.clear());
            pagehideBound = true;
        }
    }
    armGestureUnlock();
    return el;
}

function stopFade(): void {
    if (fadeTimer) { clearInterval(fadeTimer); fadeTimer = null; }
}

function fadeTo(target: number, onDone?: () => void): void {
    stopFade();
    const a = el;
    if (!a) { onDone?.(); return; }
    const start = a.volume;
    const t0 = performance.now();
    fadeTimer = setInterval(() => {
        const k = Math.min(1, (performance.now() - t0) / FADE_MS);
        a.volume = clampVol(start + (target - start) * k);
        if (k >= 1) { stopFade(); onDone?.(); }
    }, 30);
}

function tryPlay(): void {
    if (!el || !activeUrl || !unlocked) return;
    // Autoplay may still be blocked; it retries on the next unlock()/ensure().
    el.play().catch(() => { /* blocked until a user gesture */ });
}

export const crossSceneAudio = {
    /** Start/keep/switch the ambient bed for the current scene. Idempotent for the same url. */
    ensure(url: string, opts: CrossSceneAudioOptions = {}): void {
        if (!url) return;
        if (opts.volume !== undefined) targetVolume = clampVol(opts.volume);
        const a = ensureEl();
        a.loop = opts.loop !== false;
        if (activeUrl === url) {
            // Same bed already active — keep it playing seamlessly; just refresh volume.
            if (!fadeTimer) a.volume = effectiveVolume();
            tryPlay();
            return;
        }
        const switchTrack = (): void => {
            activeUrl = url;
            a.src = url;
            a.load();
            a.volume = 0;
            tryPlay();
            fadeTo(effectiveVolume());
        };
        if (activeUrl && opts.fade !== false && !a.paused) {
            fadeTo(0, switchTrack); // fade out the old track, then switch + fade in
        } else {
            switchTrack();
        }
    },
    /** Call on the first user gesture (unmute/tap) so autoplay-blocked audio can start. */
    unlock(): void {
        disarmGestureUnlock();
        unlocked = true;
        tryPlay();
    },
    setMuted(m: boolean): void {
        muted = m;
        if (el && !fadeTimer) el.volume = effectiveVolume();
    },
    setVolume(v: number): void {
        targetVolume = clampVol(v);
        if (el && !fadeTimer) el.volume = effectiveVolume();
    },
    /** Full stop + release. Auto-fires on pagehide; hosts may call it on teardown. */
    clear(): void {
        stopFade();
        if (el) {
            el.pause();
            try { el.src = ''; } catch { /* ignore */ }
        }
        el = null;
        activeUrl = null;
        unlocked = false;
        // Re-arm: a new bed on the same page must be able to self-unlock again.
        disarmGestureUnlock();
    },
    isActive(): boolean { return !!activeUrl; },
    /** The live bed element (or null) — lets the audio-reactive analyser tap the signal. */
    getElement(): HTMLAudioElement | null { return el; },
    /** Current mute state — persists across viewer recreation, so a teleport can restore the button. */
    isMuted(): boolean { return muted; },
    /** Whether a user gesture has unlocked autoplay — lets the "Tap for audio" prompt decide to show. */
    isUnlocked(): boolean { return unlocked; },
    getUrl(): string | null { return activeUrl; },
};
